import { useState } from "react";
import { Check, Crown, Zap, Rocket, X, Loader2 } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════
// PAYMENT WALL — Premium Pricing Modal
// ═══════════════════════════════════════════════════════════════════════
// Triggers when a free-tier user tries to access premium features.
// Beautiful 3-tier pricing that converts. No escape without choosing.
// ═══════════════════════════════════════════════════════════════════════

interface PaymentWallProps {
  onClose: () => void;
  osName: string;
  subdomain?: string;
  email?: string;
  osId?: string;
}

const TIERS = [
  {
    id: "free" as const,
    name: "Explorer",
    price: "$0",
    period: "forever",
    description: "See what your Agentic Operating System could become",
    color: "#64748B",
    icon: Zap,
    features: [
      "View-only Agentic Operating System dashboard",
      "3 Operator messages",
      "Schema preview",
      "Community support",
    ],
    excluded: [
      "Agentic Evolution Engine",
      "Integrations",
      "Data editing",
      "Priority support",
    ],
    cta: "Current Plan",
    disabled: true,
  },
  {
    id: "pro" as const,
    name: "Pro",
    price: "$49",
    period: "/month",
    description: "Run your OS on Sloe Agentic Operating System",
    color: "#6366F1",
    icon: Crown,
    popular: true,
    features: [
      "Full Agentic Operating System access",
      "Unlimited Operator access",
      "5 approved integrations",
      "Agentic Evolution Engine",
      "Data editing (CRUD)",
      "Email support",
    ],
    excluded: [],
    cta: "Start Pro",
    disabled: false,
  },
  {
    id: "enterprise" as const,
    name: "Enterprise",
    price: "$199",
    period: "/month",
    description: "Scale with unlimited power",
    color: "#F59E0B",
    icon: Rocket,
    features: [
      "Everything in Pro",
      "Unlimited integrations",
      "White-label branding",
      "API access",
      "Priority evolution",
      "Dedicated support",
      "Custom data models",
    ],
    excluded: [],
    cta: "Start Enterprise",
    disabled: false,
  },
];

export function PaymentWall({ onClose, osName, subdomain, email, osId }: PaymentWallProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleCheckout = async (tier: "pro" | "enterprise") => {
    setCheckoutError(null);
    setLoading(tier);
    try {
      const res = await fetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier, email, os_id: osId, subdomain }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError(
          data.error ||
            "We couldn't create the checkout session. Please try again or contact support."
        );
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setCheckoutError(
        "We couldn't reach the payment system. Check your connection and try again."
      );
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/80 backdrop-blur-md p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-xs font-bold tracking-wider mb-4">
            <Zap size={12} /> UNLOCK {osName.toUpperCase()}
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Ready to run your OS on autopilot?
          </h2>
          <p className="text-slate-400 max-w-lg mx-auto">
            Your Agentic Operating System is built. Your agents are ready. Choose your plan and make the workspace permanent.
          </p>
        </div>

        {checkoutError && (
          <div className="mb-5 flex items-start justify-between gap-3 rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            <div>
              <div className="font-semibold text-rose-50">Checkout needs attention</div>
              <p className="mt-1 text-xs leading-relaxed text-rose-100/80">{checkoutError}</p>
            </div>
            <button
              type="button"
              onClick={() => setCheckoutError(null)}
              className="rounded-lg p-1 text-rose-100/70 transition-colors hover:bg-white/10 hover:text-rose-50"
              aria-label="Dismiss checkout error"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            const isPopular = tier.popular;
            const isLoading = loading === tier.id;
            
            return (
              <div
                key={tier.id}
                className={`relative rounded-2xl p-6 flex flex-col transition-all duration-300 ${
                  isPopular
                    ? "bg-white shadow-2xl shadow-indigo-500/20 scale-[1.02] border-2 border-indigo-500"
                    : "bg-white/10 backdrop-blur-sm border border-white/10 hover:border-white/20"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                    Most Popular
                  </div>
                )}

                <div className="mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${tier.color}20` }}
                  >
                    <Icon size={20} style={{ color: tier.color }} />
                  </div>
                  <h3 className={`text-lg font-bold ${isPopular ? "text-slate-900" : "text-white"}`}>
                    {tier.name}
                  </h3>
                  <p className={`text-xs mt-1 ${isPopular ? "text-slate-500" : "text-slate-400"}`}>
                    {tier.description}
                  </p>
                </div>

                <div className="mb-6">
                  <span className={`text-4xl font-bold ${isPopular ? "text-slate-900" : "text-white"}`}>
                    {tier.price}
                  </span>
                  <span className={`text-sm ${isPopular ? "text-slate-500" : "text-slate-400"}`}>
                    {tier.period}
                  </span>
                </div>

                <div className="flex-1 mb-6">
                  {tier.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2 py-1.5">
                      <Check
                        size={14}
                        className={isPopular ? "text-indigo-600" : "text-emerald-400"}
                      />
                      <span className={`text-sm ${isPopular ? "text-slate-700" : "text-slate-300"}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                  {tier.excluded.map((feature) => (
                    <div key={feature} className="flex items-center gap-2 py-1.5 opacity-40">
                      <X size={14} className="text-slate-400" />
                      <span className="text-sm text-slate-400 line-through">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    if (tier.id === "pro" || tier.id === "enterprise") {
                      handleCheckout(tier.id);
                    }
                  }}
                  disabled={tier.disabled || isLoading}
                  className={`w-full py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    isPopular
                      ? "bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/30"
                      : tier.disabled
                        ? "bg-white/5 text-slate-500 cursor-default"
                        : "bg-white/10 hover:bg-white/20 text-white border border-white/10"
                  }`}
                >
                  {isLoading ? (
                    <><Loader2 size={16} className="animate-spin" /> Redirecting...</>
                  ) : (
                    tier.cta
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <button
            onClick={onClose}
            className="text-xs text-slate-500 hover:text-white transition-colors"
          >
            Continue with free tier (limited features)
          </button>
        </div>
      </div>
    </div>
  );
}
